'use client';
import { crudNotFound } from '@bells/i18n';
import type { WithId } from '@measured/puck';
import { first, slice } from 'lodash';
import { FileSearch } from 'lucide-react';
import { useMemo } from 'react';
import { BlogItem } from '../../../components/blog-item';
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
} from '../../../components/carousel';
import type { BlogOverviewProps } from './blog-overview.props';

const BlogOverview = ({
  items,
  enabled = true,
  id,
}: WithId<Pick<BlogOverviewProps, 'items'>> & { enabled?: boolean }) => {
  const [main, chunked, chunkedUnder] = useMemo(() => {
    const mainItem = first(items);
    const chunkedItems = slice(items, 1, 4);
    const chunkedUnderItems = slice(items, 4);
    return [mainItem, chunkedItems, chunkedUnderItems];
  }, [items]);

  const Overview = useMemo(() => {
    return items && items?.length > 0 ? (
      <Carousel
        opts={{
          align: 'start',
          watchDrag: enabled,
          breakpoints: { '(min-width: 1420px)': { watchDrag: false } },
          loop: false,
        }}
        className='min-h-0 w-full min-w-0 max-w-full'
      >
        <CarouselContent className='-ml-2 min-h-0 min-w-0 xl:m-0 xl:grid xl:grid-cols-4 xl:gap-4'>
          {main && (
            <CarouselItem className='basis-[80%] sm:basis-[45%] md:basis-[28%] xl:col-span-2 xl:row-span-3 xl:basis-0 xl:p-0'>
              <BlogItem item={main}></BlogItem>
            </CarouselItem>
          )}
          {(chunked ?? []).map((item) => {
            return (
              <CarouselItem
                key={`${id}-${item.id}`}
                className='basis-[80%] sm:basis-[45%] md:basis-[28%] xl:col-span-2 xl:col-start-3 xl:basis-0 xl:p-0'
              >
                <BlogItem
                  className='gap-x-4 xl:grid xl:h-auto xl:grid-cols-2 xl:flex-row'
                  item={item}
                ></BlogItem>
              </CarouselItem>
            );
          })}

          {(chunkedUnder ?? []).map((item) => {
            return (
              <CarouselItem
                key={`${id}-${item.id}`}
                className='col-span-1 basis-[80%] sm:basis-[45%] md:basis-[28%] xl:basis-0 xl:p-0 xl:pt-4'
              >
                <BlogItem item={item}></BlogItem>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <CarouselDots className='hidden lg:inline-flex'></CarouselDots>
      </Carousel>
    ) : (
      <div className='flex w-full flex-col items-center gap-2 text-center text-muted-foreground'>
        <FileSearch className='h-12 w-12'></FileSearch>
        <span className='text-base'>{crudNotFound()}</span>
      </div>
    );
  }, [main, chunked, items]);

  return Overview;
};

export { BlogOverview };
